import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { FormInput, FormButton, Margin, Form, Text } from 'shared/components'

import { TeamsPlayersComponent } from '../teamsPlayers'

import { getOptionLabel, getOptionValue, calcTotalAmount } from './helpers'

import * as styles from './styles'

const EditMakeInwinFormComponent = ({
  header,
  loadSponsors,
  loadBeneficiaries,
  loadTriggers,
  form,
  onSubmitRequest,
  initialValues,
}) => {
  const [sponsorLimit, setSponsorLimit] = useState(0)
  const [formValid, setFormValid] = useState(false)

  // Centralized validation function to ensure consistent validation
  const validateForm = () => {
    const hasBeneficiary = !!form.fields.beneficiary.value;
    const hasAmount = !!form.fields.amount.value;
    const hasTriggers = !!form.fields.triggers.value;

    setFormValid(hasBeneficiary && hasAmount && hasTriggers);
    return hasBeneficiary && hasAmount && hasTriggers;
  };

  // Initialize form validation check on mount
  useEffect(() => {
    validateForm();
  }, []);

  return (
    <>
      <Margin tb={4}>
        <Text css={styles.heading}>{header}</Text>
      </Margin>
      <Form form={form} onSubmit={onSubmitRequest}>
        <Margin bottom={2} css={styles.row}>
          <TeamsPlayersComponent
            teamOne={initialValues.eventData.teamOne}
            teamTwo={initialValues.eventData.teamTwo}
            startTime={initialValues.eventData.startTime}
            tournament={initialValues.eventData.tournamentDTO}
            matchName={initialValues.eventData.matchName}
          />
        </Margin>
        <Margin bottom={2} css={styles.row}>
          <Text as="label" size={16} css={styles.fieldLabel}>
            Beneficiary
          </Text>
          <FormInput
            cacheOptions
            defaultOptions
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            type="async-select"
            name="beneficiary"
            placeholder="Select Beneficiary"
            loadOptions={loadBeneficiaries}
            styles={styles.select}
            onChange={(val) => {
              form.fields.beneficiary.onChange(val);
              // Run validation after change
              validateForm();
            }}
            oldInput
            bordered
            fluid
          />
        </Margin>

        <Margin bottom={2} css={styles.row}>
          <Text as="label" size={16} css={styles.fieldLabel}>
            Amount
          </Text>
          <FormInput
            name="amount"
            placeholder="Amount"
            onChange={(val) => {
              form.fields.amount.onChange(val)
              calcTotalAmount(form)
              // Run validation after change
              validateForm();
            }}
            css={styles.control}
          />
        </Margin>

        <Margin bottom={2} css={styles.row}>
          <Text as="label" size={16} css={styles.fieldLabel}>
            Triggers
          </Text>
          <FormInput
            cacheOptions
            defaultOptions
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            isSearchable={false}
            type="async-select"
            name="triggers"
            placeholder="Select Triggers"
            loadOptions={loadTriggers}
            styles={styles.select}
            onChange={(val) => {
              form.fields.triggers.onChange(val);
              // Run validation after change
              validateForm();
            }}
            oldInput
            bordered
            fluid
          />
        </Margin>

        <Margin bottom={2} css={styles.row}>
          <Text as="label" size={16} css={styles.fieldLabel}>
            Sponsor
          </Text>
          <FormInput
            cacheOptions
            isClearable
            defaultOptions
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            type="async-select"
            name="sponsors"
            placeholder="Sponsors (Optional)"
            loadOptions={loadSponsors}
            styles={styles.select}
            onChange={(val) => {
              form.fields.sponsors.onChange(val)
              if (val.target.value === null) {
                form.fields.sponsorAmount.onChange({ target: { value: 0 } })
                calcTotalAmount(form)
              }
              setSponsorLimit(val.target.value?.limit || 0)

              // Run validation after change
              validateForm();
            }}
            oldInput
            bordered
            fluid
          />
        </Margin>

        {sponsorLimit > 0 ? (
          <Margin bottom={2} css={styles.row}>
            <Text as="label" size={16} css={styles.fieldLabel}>
              Sponsor Amount
            </Text>
            <FormInput
              name="sponsorAmount"
              placeholder="Amount"
              css={styles.control}
              onChange={(val) => {
                form.fields.sponsorAmount.onChange(val)
                calcTotalAmount(form)
              }}
            />
          </Margin>
        ) : null}

        {sponsorLimit > 0 ? (
          <Margin bottom={3}>
            <Text as="label" size={14} italic css={styles.fieldLabel}>
              {`Sponsor's limit is ${sponsorLimit}`}
            </Text>
          </Margin>
        ) : null}

        <Margin bottom={2} css={styles.row}>
          <Text as="label" size={16} css={styles.fieldLabel}>
            Total Amount
          </Text>
          <FormInput
            name="totalAmount"
            placeholder="Total Amount"
            disabled={true}
            css={styles.control}
          />
        </Margin>

        <Margin top={3} css={styles.buttonContainer}>
          <FormButton type="submit" medium variation="primary" disabled={!formValid}>
            <Text size={16} upper bold black>
              CONFIRM INWIN
            </Text>
          </FormButton>
        </Margin>
      </Form>
    </>
  )
}

EditMakeInwinFormComponent.propTypes = {
  form: PropTypes.shape(),
  header: PropTypes.string.isRequired,
  initialValues: PropTypes.shape(),
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  loadCategories: PropTypes.func,
  loadBeneficiary: PropTypes.func,
  onSubmitRequest: PropTypes.func,
  loadSponsors: PropTypes.func,
  loadBeneficiaries: PropTypes.func,
  loadTriggers: PropTypes.func,
}

export default EditMakeInwinFormComponent
